// 2026-07-02 US-003: view models for the bulk notifications page

export enum NotifChannel {
  Email = 'Email',
  SMS = 'SMS',
}

// Projected per-recipient channel for the preview table (no real delivery status yet)
export enum ResolvedChannel {
  Email = 'Email',
  Sms = 'SMS',
  SmsFallback = 'SMS-fallback',
  EmailFallback = 'Email-fallback',
  Unreachable = 'Unreachable',
}

// Mutually exclusive recipient audience selection — mirrors the backend contract
// (BulkNotificationService requires exactly one of ContainerId / RefNumbers).
export enum RecipientMode {
  Container = 'Container',
  Reference = 'Reference',
}

// Request/response shapes for KFSEndpoints.BulkNotification_GetRecipients.
export interface GetRecipientsRequest {
  ContainerId?: string;
  RefNumbers?: string[];
}

// 2026-08-09 US-005: destination groups, resolved server-side from the receiver's address
// against DeliveryAreaRate. Unknown is a real bucket, not an error — it means the stored
// province could not be classified, which in practice means it was hand-typed via the
// booking form's "I can't find my Town or Province" option.
export enum Destination {
  MetroManila = 'Metro Manila',
  Luzon = 'Luzon',
  Visayas = 'Visayas',
  Mindanao = 'Mindanao',
  Unknown = 'Unknown',
}

// Filter/display order — Unknown deliberately last.
export const DESTINATION_ORDER: string[] = [
  Destination.MetroManila,
  Destination.Luzon,
  Destination.Visayas,
  Destination.Mindanao,
  Destination.Unknown,
];

// How the destination was arrived at. 'Derived' means a town name was typed into the
// province field and resolved to exactly one region — shown as such so an operator can see
// it was inferred rather than matched outright.
export enum MatchType {
  Province = 'Province',
  Derived = 'Derived',
  Unknown = 'Unknown',
}

export interface RecipientApiResult {
  RefNo: string;
  CustomerName: string;
  Email: string | null;
  Mobile: string | null;
  // 2026-08-09 US-005: raw receiver address as stored, shown verbatim so an operator can
  // recognise an unclassifiable place by eye. Optional so a build running against the
  // pre-US-005 stored procedure degrades to "Unknown" rather than crashing.
  Province?: string | null;
  Town?: string | null;
  Destination?: string | null;
  MatchType?: string | null;
}

// All-or-nothing: IsValid is false only in Reference mode when some ref(s) didn't
// match — Recipients is empty in that case, InvalidRefNumbers lists the bad ones.
export interface GetRecipientsResponse {
  IsValid: boolean;
  InvalidRefNumbers: string[];
  Recipients: RecipientApiResult[];
}

// Client-side row after channel projection (Email/SMS/fallback/Unreachable) is applied
// to a RecipientApiResult for the currently selected channel + fallback setting.
export interface RecipientRow {
  RefNo: string;
  CustomerName: string;
  Resolved: ResolvedChannel;
  Email: string | null;
  Mobile: string | null;
  // 2026-08-09 US-005: always populated (never undefined) — an unclassified or
  // pre-US-005 payload resolves to Destination.Unknown so filtering and select-all can
  // treat it uniformly without null checks.
  Destination: string;
  MatchType: string;
  Province: string;
  Town: string;
}

// 2026-07-02 US-003: real sending. Audience is re-sent as the same selector used for
// GetRecipients (never the resolved recipient list) — the backend re-resolves it.
export interface SendRequest {
  ContainerId?: string;
  RefNumbers?: string[];
  // 2026-08-09 US-005: the ticked reference numbers. The backend re-resolves the audience
  // itself and INTERSECTS it with this list — it is a narrowing filter, never the audience.
  SelectedRefNos?: string[];
  Channel: NotifChannel;
  FallbackOn: boolean;
  Subject: string;
  EmailBody: string;
  SmsBody: string;
  // Optional PDF attachment, Email-channel only. Backend independently re-validates
  // (extension, magic bytes, size) rather than trusting these client-side checks.
  AttachmentFileName?: string;
  AttachmentBase64?: string;
}

export enum SendStatus {
  Pending = 'Pending',
  InProgress = 'InProgress',
  Completed = 'Completed',
  CompletedWithErrors = 'CompletedWithErrors',
}

// Send runs synchronously in the request (see BulkNotificationService.Send), so this
// carries the real final outcome, not just an id to poll for later.
export interface SendResponse {
  SendId: string;
  Status: SendStatus;
  TotalRecipients: number;
  SentCount: number;
  FailedCount: number;
}

export interface HistoryItem {
  SendId: string;
  CreatedDate: string;
  SentBy: string;
  Mode: string;
  AudienceRef: string;
  Channel: string;
  TotalRecipients: number;
  SentCount: number;
  FailedCount: number;
  Status: SendStatus;
}

export interface ResultItem {
  RefNo: string;
  CustomerName: string;
  ChannelUsed: string;
  ContactValue: string;
  Status: string;
  ErrorMessage: string | null;
}

export interface BatchDetailResponse {
  Batch: HistoryItem;
  Subject: string;
  EmailBody: string;
  SmsBody: string;
  AttachmentFileName: string | null;
  Results: ResultItem[];
}
